import {
  estadoDeTarea,
  ESTADOS_DE_TAREA,
  fechaDeTarea,
  sePuedeCambiar,
  textoDelEstado,
} from "@/pages/conversations/agent-tasks";
import type {
  AgentTaskStatus,
  FichaDeTarea,
  TaskView,
} from "@/services/conversations.api";

/**
 * La pestaña «Tareas» del drawer de un asistente (D65): lo que tiene programado en todas sus
 * conversaciones y en las fichas del historial (D66), lo que corre, lo hecho y lo fallado. Funciones puras, para poder testearlas; la
 * hora y el estado son los mismos que en el chat.
 */

/** El conversation-service acepta hasta 50 por página. */
export const TAREAS_POR_PAGINA = 20;

/** El filtro por estado, en el orden en que pasa una tarea. */
export const FILTRO_DE_ESTADO: { value: AgentTaskStatus; label: string }[] = (
  Object.keys(ESTADOS_DE_TAREA) as AgentTaskStatus[]
).map((value) => ({ value, label: ESTADOS_DE_TAREA[value].texto }));

/** `GET /agent-tasks`: la página empieza en 1 y sin estado no se filtra. */
export function consultaDeTareas(
  agentUserId: number,
  estado: AgentTaskStatus | null,
  pagina: number,
): {
  agentUserId: number;
  page: number;
  limit: number;
  status?: AgentTaskStatus;
} {
  return {
    agentUserId,
    page: Math.max(1, pagina),
    limit: TAREAS_POR_PAGINA,
    ...(estado ? { status: estado } : {}),
  };
}

/** La lista no trae el total: una página llena es que puede haber otra. */
export function hayOtraPagina(
  cantidad: number,
  limite = TAREAS_POR_PAGINA,
): boolean {
  return cantidad >= limite;
}

/** El chat abierto en esa conversación: el mismo deep-link que la campanita. */
export function linkDeLaConversacion(conversationId: string): string {
  return `/conversations?open=${encodeURIComponent(conversationId)}`;
}

/** Cómo se nombra cada ficha, y su ruta: las mismas del resto del admin. */
const FICHAS: Record<FichaDeTarea["module"], { nombre: string; ruta: string }> =
  {
    contact: { nombre: "Contacto", ruta: "/contacts" },
    quote: { nombre: "Presupuesto", ruta: "/quotes" },
    order: { nombre: "Pedido", ruta: "/orders" },
  };

/** La ficha del historial: «Presupuesto #123» con link a la ficha. `null` si el módulo no es uno de los tres. */
export function linkDeLaFicha(
  ficha: FichaDeTarea,
): { nombre: string; href: string } | null {
  const f = FICHAS[ficha.module];
  if (!f || !ficha.entityId) return null;
  return {
    nombre: `${f.nombre} #${ficha.entityId}`,
    href: `${f.ruta}/${encodeURIComponent(ficha.entityId)}`,
  };
}

export interface FilaDeTarea {
  key: string;
  fecha: string;
  estado: { texto: string; color: string };
  /** Dónde se pidió: la conversación o la ficha del historial. Sin link si no se sabe. */
  donde: { nombre: string; href: string | null };
  pidio: string;
  /**
   * Cancelar y cambiar la hora desde la pestaña: sólo las de una ficha del historial que todavía no
   * corrieron. Las de una conversación se tocan en su nota del chat; la del historial no tiene otro
   * lugar.
   */
  tocable: boolean;
  runAt: string;
}

/** La conversación, o la ficha del historial si la tarea se pidió en una nota (D66). */
function dondeSePidio(t: TaskView): FilaDeTarea["donde"] {
  if (t.conversationId)
    return {
      nombre: t.contactName?.trim() || "Conversación sin nombre",
      href: linkDeLaConversacion(t.conversationId),
    };
  const ficha = t.history ? linkDeLaFicha(t.history) : null;
  return ficha ?? { nombre: "—", href: null };
}

/** Las filas de la tabla, en el orden en que las devolvió el servicio. */
export function filasDeTareas(tareas: TaskView[], ahora: Date): FilaDeTarea[] {
  return tareas.map((t) => {
    const estado = estadoDeTarea(t, ahora);
    return {
      key: t.taskId,
      fecha: fechaDeTarea(t.runAt, ahora),
      estado: {
        texto: textoDelEstado(estado, t.cancelledByName),
        color: ESTADOS_DE_TAREA[estado]?.color ?? "default",
      },
      donde: dondeSePidio(t),
      pidio:
        t.requestedByName?.trim() ||
        (t.requestedBy != null ? `Operador ${t.requestedBy}` : "—"),
      tocable: !t.conversationId && sePuedeCambiar(estado),
      runAt: t.runAt,
    };
  });
}
